import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useRef, useState } from "react";
import "../forms/UserForm.css";
import "../forms/EquipmentForm.css";
import "../forms/AmcSchedules.css";
import "../SafetyObservation/safetyObservation.css";
import "../SafetyMeetings/safetyMeetings.css";
import "../HsePolicies/hsePolicies.css";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../src/Config";
import HsePolicyFormFields from "../HsePolicies/HsePolicyFormFields";
import { parseApiError } from "../SafetyObservation/observationUtils";
import {
  buildHsePolicyFormData,
  formatPolicyDateForInput,
  INITIAL_HSE_POLICY_FORM_DATA,
  isExistingPolicyDocument,
} from "../HsePolicies/hsePolicyUtils";

function EditHsePolicy({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { policyId } = useParams();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_HSE_POLICY_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [documents, setDocuments] = useState([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState([]);
  const [isDocumentPopupOpen, setIsDocumentPopupOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.title.trim()) validationErrors.title = "Title is required.";

    return validationErrors;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    setDocuments((prevState) => [...prevState, ...selectedFiles]);
    setErrors((prevState) => ({
      ...prevState,
      documents: "",
    }));
    event.target.value = "";
  };

  const handleDeleteDocument = (index) => {
    setDocuments((prevState) => {
      const targetDocument = prevState[index];

      if (isExistingPolicyDocument(targetDocument)) {
        setRemovedDocumentIds((prevIds) =>
          prevIds.includes(targetDocument.id)
            ? prevIds
            : [...prevIds, targetDocument.id],
        );
      }

      return prevState.filter((_, currentIndex) => currentIndex !== index);
    });
  };

  const handleCancelClick = () => {
    navigate("/hse-policies");
  };

  const handleSaveClick = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const payload = buildHsePolicyFormData(formData, documents);

      const response = await axios.put(
        `${API_URL}/api/safety/hse-policies/${policyId}`,
        payload,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      let warningMessage = "";
      if (removedDocumentIds.length > 0) {
        try {
          await Promise.all(
            removedDocumentIds.map((documentId) =>
              axios.delete(
                `${API_URL}/api/safety/hse-policies/documents/${documentId}`,
                {
                  headers: {
                    Authorization: authKey,
                  },
                },
              ),
            ),
          );
        } catch (deleteError) {
          warningMessage = parseApiError(
            deleteError,
            "Policy updated, but some documents could not be removed",
          );
        }
      }

      showSnackbar(
        warningMessage ||
          response.data?.message ||
          "HSE policy updated successfully",
        warningMessage ? "warning" : "success",
      );
      window.setTimeout(() => {
        navigate("/hse-policies");
      }, 900);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to update HSE policy"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      try {
        setIsFetching(true);
        const authKey = localStorage.getItem("authKey");
        const response = await axios.get(
          `${API_URL}/api/safety/hse-policies/${policyId}`,
          {
            headers: {
              Authorization: authKey,
            },
          },
        );

        const policy = response.data || {};

        setFormData({
          title: policy.title || "",
          category: policy.category || "",
          description: policy.description || "",
          effective_date: formatPolicyDateForInput(policy.effective_date),
        });
        setDocuments(Array.isArray(policy.documents) ? policy.documents : []);
        setRemovedDocumentIds([]);
      } catch (error) {
        showSnackbar(parseApiError(error, "Failed to fetch HSE policy"), "error");
      } finally {
        setIsFetching(false);
      }
    };

    loadPage();
  }, [policyId]);

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content meeting-form-wrapper">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">Edit HSE Policy</PageBackTitle>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            {isFetching ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <HsePolicyFormFields
                  formData={formData}
                  errors={errors}
                  onInputChange={handleInputChange}
                  fileInputRef={fileInputRef}
                  documents={documents}
                  isDocumentPopupOpen={isDocumentPopupOpen}
                  onUploadClick={handleUploadClick}
                  onFileInputChange={handleFileInputChange}
                  onToggleDocumentPopup={() =>
                    setIsDocumentPopupOpen((prevState) => !prevState)
                  }
                  onCloseDocumentPopup={() => setIsDocumentPopupOpen(false)}
                  onDeleteDocument={handleDeleteDocument}
                />
              </form>
            )}
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading || isFetching}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default EditHsePolicy;
